import {Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';

import {MaterialModule} from '../../../material.module';
import {FrontendSettingsComponent} from '../settings/frontend-settings/frontend-settings.component';
import {createImageCompositionFormValue, validateAndNormalizeImageCompositionOptions} from '../../../models/chatbot-options.model';

type FixtureState = 'runtime-not-ready' | 'consent-copy' | 'validation';

interface FixtureStateDefinition {
  key: FixtureState;
  label: string;
  description: string;
  attachmentsEnabled: boolean;
  eligibleCategoriesText: string;
  normalizedCategories: string[];
  disclaimerText: string;
}

const fixtureStateDefinitions: Record<FixtureState, FixtureStateDefinition> = {
  'runtime-not-ready': {
    key: 'runtime-not-ready',
    label: 'Runtime off',
    description: 'Activation remains disabled while review shows prepared consent copy and eligible categories.',
    attachmentsEnabled: false,
    eligibleCategoriesText: 'Kitchen\nLiving room\nOutdoor',
    normalizedCategories: ['Kitchen', 'Living room', 'Outdoor'],
    disclaimerText: 'Upload only images you are authorized to use. Images are sent only with the current request for visual matching.',
  },
  'consent-copy': {
    key: 'consent-copy',
    label: 'Consent copy',
    description: 'Shows the review-safe consent text for request-scoped image matching with uploads enabled.',
    attachmentsEnabled: true,
    eligibleCategoriesText: 'Kitchen\nHome office\nOutdoor',
    normalizedCategories: ['Kitchen', 'Home office', 'Outdoor'],
    disclaimerText: 'Uploaded images are used only to process the current visual-matching request.',
  },
  validation: {
    key: 'validation',
    label: 'Validation',
    description: 'Surfaces a catch-all category error while runtime activation still stays unavailable.',
    attachmentsEnabled: true,
    eligibleCategoriesText: 'Kitchen\nall\nOutdoor',
    normalizedCategories: ['Kitchen', 'Outdoor'],
    disclaimerText: 'Only approved uploads should be used for image composition previews, and images are sent only with the current request.',
  },
};

@Component({
  selector: 'app-chatbot-visual-fixture',
  standalone: true,
  imports: [MaterialModule, RouterLink, FrontendSettingsComponent],
  templateUrl: './chatbot-visual-fixture.component.html',
  styleUrls: ['./chatbot-visual-fixture.component.scss'],
})
export class ChatbotVisualFixtureComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);

  readonly imageCompositionRuntimeReady = false;
  readonly fixtureStates = Object.values(fixtureStateDefinitions);
  readonly expandedPanel = 'header-capabilities' as const;

  chatbotId = 'fixture-1367';
  state: FixtureState = 'runtime-not-ready';

  frontendForm!: FormGroup;
  settingsForm!: FormGroup;
  messagesForm!: FormGroup;
  localizationForm!: FormGroup;
  localizationVariantsForm: FormArray = this.fb.array([]);

  ngOnInit(): void {
    this.chatbotId = this.route.snapshot.paramMap.get('id') || this.chatbotId;

    const requestedState = this.route.snapshot.queryParamMap.get('chatbotVisualFixture');
    this.state = requestedState === 'consent-copy' || requestedState === 'validation'
      ? requestedState
      : 'runtime-not-ready';

    this.applyState(fixtureStateDefinitions[this.state]);
  }

  get activeState(): FixtureStateDefinition {
    return fixtureStateDefinitions[this.state];
  }

  get fixtureValidation() {
    return validateAndNormalizeImageCompositionOptions(
      this.frontendForm?.get('imageComposition')?.getRawValue(),
      this.frontendForm?.get('imageComposition')?.getRawValue(),
      this.frontendForm?.get('generalSettings.attachmentsEnabled')?.value === true,
      this.imageCompositionRuntimeReady
    );
  }

  private applyState(config: FixtureStateDefinition): void {
    const imageComposition = createImageCompositionFormValue({
      disclaimerText: config.disclaimerText,
      eligibleCategories: config.normalizedCategories,
    }, false);

    this.settingsForm = this.fb.group({
      aiProductCardsEnabled: [false],
      aiProductCardsInstruction: [''],
    });

    this.frontendForm = this.fb.group({
      generalSettings: this.fb.group({
        name: ['Review-safe fixture chatbot'],
        logo: ['https://example.com/logo.png'],
        headerDisplayMode: [''],
        layoutPreset: ['compact-mobile'],
        zIndex: [3001],
        logoWidth: [48],
        voiceEnabled: [false],
        attachmentsEnabled: [config.attachmentsEnabled],
        introMessage: ['Show approved image prompts for staged room updates.'],
        firstMessage: ['Upload a room photo to review supported image-composition categories.'],
        inputPlaceholder: ['Describe the update you want to preview'],
        submitButtonIcon: [''],
        resetButtonIcon: [''],
        attachButtonIcon: [''],
        voiceButtonIcon: [''],
        compactSubmitButtonIcon: [''],
        compactResetButtonIcon: [''],
        resetButtonTooltip: ['Click to reset your chat'],
        resetButtonConfirmationEnabled: [false],
        resetButtonConfirmationText: ['Click again to reset your chat'],
        sampleMessagesTitle: [''],
        sampleMessagesPresentation: [''],
        sampleMessagesHoverBorderColor: [''],
        sampleMessagesClickAnimationEnabled: [false],
        sampleMessagesClickAnimationDelayMs: [140],
      }),
      imageComposition: this.fb.group({
        enabled: [false],
        cameraCaptureEnabled: [imageComposition.cameraCaptureEnabled],
        eligibleCategoriesText: [config.eligibleCategoriesText || imageComposition.eligibleCategoriesText],
        disclaimerText: [imageComposition.disclaimerText],
        preferredRequestTransport: [imageComposition.preferredRequestTransport],
        supportedRequestTransports: [imageComposition.supportedRequestTransports],
        uploadExpiryHours: [imageComposition.uploadExpiryHours],
        resultExpiryHours: [imageComposition.resultExpiryHours],
        ownership: this.fb.group({
          uploadBoundTo: [imageComposition.ownership.uploadBoundTo],
          resultBoundTo: [imageComposition.ownership.resultBoundTo],
        }),
      }),
      productCardSettings: this.fb.group({
        cardType: ['product', Validators.required],
        productCardTextOverrides: [{}],
        productCardPresentation: ['vertical-list'],
        productCardPresentationMode: ['fixed'],
        productCardDynamicPresentations: [[]],
        addToCartEnabled: [false],
        productCardEcommerceTrackingEnabled: [false],
        ga4EcommerceEventsEnabled: [false],
        cardLinkUrl: ['https://example.com/products'],
        cardUtmString: ['utm_source=visual_fixture'],
        cardLocale: ['en-US'],
        cardCurrency: ['USD'],
        productCardDetailsLabel: ['More info'],
        productCardTestDriveLabel: ['Test drive'],
      }),
      dropdownSettings: this.fb.group({
        dropdownEnabled: [false],
        dropdownBtnText: ['Saved items'],
        dropdownBtnTextClose: ['Close'],
        dropdownMessage: ['Saved recommendations'],
        dropdownBtnIconEnabled: [false],
      }),
      chatAvatar: ['https://example.com/avatar.png'],
      chatAvatarEnabled: [true],
      styling: this.fb.group({
        advancedStylingEnabled: [true],
        chatBotWidth: ['1200'],
        font: ['Inter+Tight'],
        fontProvider: ['google'],
        fontFamily: [''],
        fontFallback: ['Inter, Arial, sans-serif'],
        fontPreset: [''],
        fontFaceRegularUrl: [''],
        fontFaceMediumUrl: [''],
        fontFaceSemiBoldUrl: [''],
        fontFaceBoldUrl: [''],
        widgetText: [''],
        textLinkColor: [''],
        chatBubbleAssistant: ['#FFFFFF'],
        chatBubbleAssistantBorderRadiusDesktop: ['0 12px 12px 12px'],
        chatBubbleAssistantBorderRadiusMobile: ['12px 12px 12px 0'],
        chatBubbleAssistantFontSizeDesktop: ['16px'],
        chatBubbleAssistantFontSizeMobile: ['14px'],
        chatBubbleUser: ['#000000'],
        chatBubbleUserBorderRadius: ['12px 12px 0 12px'],
        avatarAccent: ['#CCCCCC'],
        chatAvatarSizeDesktop: ['50px'],
        chatAvatarSizeMobile: ['16px'],
        ctaPrimaryBackground: ['#0000FF'],
        ctaPrimaryHover: ['#0000AA'],
        ctaPrimaryText: ['#FFFFFF'],
        ctaPrimaryTextHover: ['#EEEEEE'],
        badgeBackground: ['#FFFFFF'],
        badgeText: ['#000000'],
        inputBackground: ['#FFFFFF'],
        inputPlaceholder: ['#CCCCCC'],
        inputBorderColor: ['#D0D5DD'],
        inputHoverBorderColor: ['#121413'],
        inputActiveBorderColor: [''],
        inputActiveSendBackground: ['#121413'],
        inputActiveSendColor: ['#FFFFFF'],
        inputFocus: ['#AAAAAA'],
        inputShadowEnabled: [false],
        dpBtnBgColor: ['#FFFFFF'],
        dpBtnBgColorHover: ['#F0F0F0'],
        dpBtnColor: ['#000000'],
        dpBtnColorHover: ['#333333'],
        recIconColor: ['#444444'],
        recIconColorHover: ['#222222'],
        resetButton: ['#FF0000'],
        resetButtonHoverBackground: ['#E31100'],
        resetButtonHoverColor: ['#FFFFFF'],
        resetButtonPressedBackground: ['#121413'],
        resetButtonPressedColor: ['#FFFFFF'],
        closeButton: ['#FF0000'],
        sugBgColor: ['#FFFFFF'],
        sugIconBgColor: ['#EEEEEE'],
        sugIconColor: ['#111111'],
        cardBtnBgColor: ['#FFFFFF'],
        cardBtnBgColorHover: ['#F5F5F5'],
        cardBtnColor: ['#000000'],
        cardBtnColorHover: ['#333333'],
        cardBtnBorderColor: ['#FFFFFF'],
        cardBtnBorderColorHover: ['#F5F5F5'],
        cardAddToCartBgColor: ['#FFFFFF'],
        cardAddToCartBgColorHover: ['#F5F5F5'],
        cardAddToCartColor: ['#000000'],
        cardAddToCartColorHover: ['#333333'],
        cardAddToCartBorderColor: ['#FFFFFF'],
        cardAddToCartBorderColorHover: ['#F5F5F5'],
        cardWidth: [''],
        cardMaxWidth: ['273px'],
        cardBorderColor: ['transparent'],
        cardBackgroundColor: ['transparent'],
        cardPadding: ['1rem 0 0'],
        cardTitleFontSize: ['19px'],
        cardTitleFontWeight: ['600'],
        cardDescriptionFontSize: ['15px'],
        cardDescriptionVisible: [true],
        cardCtaVisible: [true],
        cardPriceVisible: [true],
        cardPriceFontSize: ['17px'],
        cardPriceFontWeight: ['700'],
        cardPriceCurrentColor: ['#b42318'],
        cardPriceSaleCurrentColor: ['#b42318'],
        cardPriceOriginalColor: ['#111827'],
        cardPriceOriginalFontSize: ['15px'],
        cardImageAspectRatio: ['3:4'],
        cardCompactImageMaxHeight: [''],
        cardImageBackgroundColor: ['#f3f4f6'],
        cardBorderRadius: ['8px'],
        cardBadgeBackgroundColor: ['transparent'],
        cardBadgeTextColor: ['#121413'],
        cardSaleBadgeTextColor: ['#e31100'],
        cardAttributeBackgroundColor: ['#f4f4f4'],
        cardAttributeTextColor: ['#121413'],
      }),
      css: [''],
      js: [''],
    });

    this.messagesForm = this.fb.group({
      sampleMessages: this.fb.array([
        this.fb.group({
          headline: ['Approved room refresh'],
          description: ['Preview image composition only for eligible categories.'],
          imageUrl: [''],
          imageAlt: [''],
        }),
      ]),
      errorMessages: this.fb.array([
        this.fb.group({
          message: ['Image composition runtime is not available yet.'],
        }),
      ]),
      waitMessages: this.fb.array([
        this.fb.group({
          message: ['Preparing review-safe image composition guidance.'],
        }),
      ]),
    });

    this.localizationForm = this.fb.group({
      defaultLocale: ['en-US'],
      supportedLocales: [['en-US']],
      autoGenerate: [false],
    });

    this.localizationVariantsForm = this.fb.array([]);
  }
}
